import React, { useEffect, useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js";
import { useParams } from "react-router-dom";
import axios from "axios";
import { apiUrl } from "../../config/config";
import { toast } from "react-toastify";
import { GoArrowLeft } from "react-icons/go";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement
);

function WellnessDashboard() {
  const [activeTab, setActiveTab] = useState("factors");
  const { customerId } = useParams(); // Get customerId from URL
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sleepQualityAnalysis, setSleepQualityAnalysis] = useState(false);
  const [showDemoDataNotification, setshowDemoDataNotification] =
    useState(false);
  const [welnessActivity, setWelnessActivity] = useState();
  const navigate = useNavigate();
  const activityData = dashboardData[0]?.result?.activity;

  const activityEntries = welnessActivity
    ? Object.entries(welnessActivity)
        .filter(
          ([name]) => name !== "Unknown" && name !== "Done!" && name !== "Skip"
        )
        .map(([name, count]) => ({ name, count }))
    : [];

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 30);
  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  useEffect(() => {
    if (!customerId) {
      setshowDemoDataNotification(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerId) return;

      try {
        const response = await axios.post(
          `${apiUrl}/api/v1/wellness/quiz/get/date`,
          {
            customerId,
            startdate: startDate,
            enddate: endDate,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        setWelnessActivity(response.data.activityCounts);
        if (response?.data?.success === true) {
          setDashboardData(response?.data?.entries);
        } else {
          setshowDemoDataNotification(true);
          toast.dismiss();
          toast.error(response?.data?.message);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customerId, startDate, endDate]);

  function generateLineChartData(supplementData) {
    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0);
    const labels = [];
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setUTCDate(date.getUTCDate() - i);
      labels.push(date.toLocaleDateString("en-US", { weekday: "short" }));
      dates.push(date);
    }

    const normalizedData = supplementData?.map((entry) => {
      const date = new Date(entry.date);
      date.setUTCHours(0, 0, 0, 0);
      return { date, supplement: entry.result?.supplement };
    });

    const supplementValues = dates?.map((date) => {
      const entry = normalizedData.find(
        (d) => d.date.getTime() === date.getTime()
      );
      if (!entry || entry.supplement === null || entry.supplement === undefined)
        return 0;
      return entry.supplement.toLowerCase() === "yes" ? 1 : -1;
    });

    return {
      labels: labels,
      datasets: [
        {
          label: "Supplement Usage",
          data: supplementValues,
          borderColor: "green",
          backgroundColor: "rgba(0, 128, 0, 0.2)",
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }

  const defaultChartData = {
    labels: ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"],
    datasets: [
      {
        label: "Supplement Usage",
        data: [0, 0, 0, 0, 0, 1, 1],
        borderColor: "gray",
        backgroundColor: "rgba(128, 128, 128, 0.2)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const [lineChartData, setLineChartData] = useState(defaultChartData);

  useEffect(() => {
    if (dashboardData?.length) {
      setLineChartData(generateLineChartData(dashboardData));
    }
  }, [dashboardData]);

  const factorsData = {
    labels: ["Factor A", "Factor B", "Factor C", "Factor D"],
    datasets: [
      {
        label: "Impact Score",
        data: [65, 59, 80, 81],
        backgroundColor: ["red", "orange", "purple", "blue"],
      },
    ],
  };

  const trendsData = {
    labels: ["Jan", "Feb", "Mar", "Apr"],
    datasets: [
      {
        label: "Trend Score",
        data: [45, 72, 58, 90],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
    ],
  };
  const barChartData = activeTab === "factors" ? factorsData : trendsData;

  function calculateStreak(data) {
    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0);
    const entries = data
      ?.map((entry) => {
        const date = new Date(entry.date);
        date.setUTCHours(0, 0, 0, 0);
        return { date, supplement: entry?.result?.supplement };
      })
      ?.sort((a, b) => b.date - a.date);

    if (!entries || entries.length === 0) return 0;

    let streak = 0;
    let previousDate = currentDate;

    for (let i = 0; i < entries.length; i++) {
      const currentEntry = entries[i];
      const diffInDays =
        (previousDate - currentEntry.date) / (1000 * 60 * 60 * 24);

      const supplementValue = currentEntry.supplement?.toLowerCase();

      if (diffInDays === 0 && supplementValue === "yes") {
        streak = 1;
      } else if (diffInDays === 1 && supplementValue === "yes") {
        streak++;
        previousDate = currentEntry.date;
      } else {
        break;
      }
    }

    return streak;
  }

  const streak = calculateStreak(dashboardData);
  function getDaysInMonth() {
    const now = new Date(Date.now());
    const year = now.getFullYear();
    const month = now.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }

  const getDayColor = (date, data) => {
    const entry = data.find((d) => {
      const entryDate = new Date(d.date);
      entryDate.setUTCHours(0, 0, 0, 0);
      return entryDate.getTime() === date.getTime();
    });

    if (!entry || !entry.result?.supplement) return "bg-red-500";
    return entry.result.supplement.toLowerCase() === "yes"
      ? "bg-green-500"
      : "bg-yellow-300";
  };

  const daysInMonth = getDaysInMonth();

  const currentDate = new Date();
  currentDate.setUTCHours(0, 0, 0, 0);
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const date = new Date(currentDate);
    date.setUTCDate(date.getUTCDate() - (29 - i));
    return date;
  });

  function calculateAdherenceRate(data) {
    const currentDate = new Date();
    currentDate.setUTCHours(0, 0, 0, 0);

    const thirtyDaysAgo = new Date(currentDate);
    thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 29);
    const relevantEntries = data
      ?.map((entry) => {
        const date = new Date(entry.date);
        date.setUTCHours(0, 0, 0, 0);
        return { date, supplement: entry?.result?.supplement };
      })
      .filter(
        (entry) => entry.date >= thirtyDaysAgo && entry.date <= currentDate
      );

    if (!relevantEntries || relevantEntries.length === 0) return 0;

    const yesDays = new Set(
      relevantEntries
        .filter((entry) => entry.supplement?.toLowerCase() === "yes")
        .map((entry) => entry.date.getTime())
    );

    const totalDays = 30;
    const adherenceRate = (yesDays.size / totalDays) * 100;

    return Math.round(adherenceRate);
  }
  const adherence = calculateAdherenceRate(dashboardData);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex  w-full  justify-center items-center relative py-3  bg-[#2d3d23] text-white">
        <div className="w-11/12 relative gap-2  flex items-center justify-center ">
          <img
            src={`https://thehealthspanco.com/cdn/shop/files/Logo_280x100px.png?v=1706525837&width=240`}
            className={` w-24 lg:w-32   brightness-[15000%] cursor-pointer`}
            alt="logo"
            onClick={() => {
              window.location.href = "https://thehealthspanco.com/";
            }}
          />
        </div>
      </div>

      <div className="max-w-6xl w-full flex flex-col justify-center items-center p-6 min-h-screen mx-auto">
        <div className="flex flex-col gap-4 md:flex-row justify-between w-full items-center mb-6">
          <div className="flex">
            <button
              onClick={() => {
                if (window.history.length > 2) {
                  navigate(-1);
                } else {
                  window.location.href = "https://thehealthspanco.com/";
                }
              }}
              className="border border-[#b7d0a8] text-[#2d3d23] hover:text-white hover:bg-[#2F412F] px-2 rounded-[50%] flex items-center hover:font-normal mr-3
"
            >
              <GoArrowLeft className="mr-1" />
            </button>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold self-start">
              Wellness Dashboard
            </h1>
          </div>
          <div className="flex gap-5 text-xs text-[#2d3d23]">
            <div className="px-4 py-2 border border-[#2d3d23] rounded-lg">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate}
                className="bg-transparent"
              />
            </div>
            <div className="px-4 py-2 border border-[#2d3d23] rounded-lg">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={today.toISOString().split("T")[0]}
                className="bg-transparent"
              />
            </div>
          </div>
        </div>

        {showDemoDataNotification === true && (
          <div className="w-full text-lg md:text-xl lg:text-2xl text-[#2F412F] font-bold p-4 border-2 border-[#2F412F] rounded mb-3 items-center">
            This is the demo data, your data will look like this, Please take
            the quiz.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-white p-4 rounded-lg shadow w-full">
          <div className="bg-[#2F412F] text-white p-6 flex flex-col gap-1 md:gap-2 rounded-lg text-center">
            <h2 className="text-lg font-semibold">Current Streak</h2>
            <p className="text-3xl font-bold">{streak || 1}</p>
            <p>days</p>
          </div>
          <div className="bg-[#2F412F] text-white p-6 flex flex-col gap-1 md:gap-2 rounded-lg text-center">
            <h2 className="text-lg font-semibold">Adherence Rate</h2>
            <p className="text-3xl font-bold">{adherence || 5}%</p>
            <p>this month</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="bg-white p-4 rounded-lg shadow h-[450px] flex flex-col">
            <h2 className="text-xl font-bold mb-5 text-[#2F412F]">
              Wellness Overview
            </h2>
            <div className="flex-1">
              <Line
                data={lineChartData}
                options={{ maintainAspectRatio: false, responsive: true }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow h-[450px] flex flex-col">
            <h2 className="text-xl font-bold mb-5 text-[#2F412F]">
              Sleep Quality Analysis
            </h2>

            <div className="flex gap-4 mb-4">
              <button
                className={`px-4 py-2  hover:bg-white font-semibold border-b-2 ${
                  activeTab === "factors"
                    ? " border-[#2F412F] text-[#2F412F]"
                    : "text-gray-600 border-white"
                }`}
                onClick={() => setActiveTab("factors")}
              >
                Factors
              </button>
              <button
                className={`px-4 py-2  hover:bg-white font-semibold border-b-2  ${
                  activeTab === "trends"
                    ? " border-[#2F412F] text-[#2F412F]"
                    : "text-gray-600 border-white"
                }`}
                onClick={() => setActiveTab("trends")}
              >
                Trends
              </button>
            </div>
            {(activeTab === "factors" || activeTab === "trends") && (
              <div className="flex-1 relative flex justify-center">
                <div className="relative transition-all h-full">
                  {!dashboardData?.result?.sleepCheck && (
                    <div className="absolute inset-0 bg-black/50 z-10"></div>
                  )}
                  {activeTab === "factors" ? (
                    <Bar
                      data={barChartData}
                      options={{ maintainAspectRatio: false, responsive: true }}
                    />
                  ) : (
                    <Line
                      data={lineChartData}
                      options={{ maintainAspectRatio: false, responsive: true }}
                    />
                  )}
                </div>
                {!dashboardData?.result?.sleepCheck && (
                  <div className="absolute text-white z-[999] top-28 text-lg font-semibold">
                    Data will appear
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-5 text-[#2F412F]">
              Wellness Activities
            </h2>
            {activityEntries.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {activityEntries.map((activity, index) => (
                  <div
                    key={index}
                    className="bg-gray-200 p-4 rounded-md text-center shadow"
                  >
                    <p className="text-xl font-bold">{activity.count}</p>
                    <p className="text-sm">{activity.name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 py-4">
                Your data will appear after 7 days.
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 w-full">
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-5 text-[#2F412F]">
              Last 30 Days Progress
            </h2>
            <div className="grid grid-cols-6 md:grid-cols-7 gap-3 text-black text-center">
              {last30Days?.map((date, index) => (
                <div
                  key={index}
                  className={`w-full aspect-square flex items-center justify-center rounded-full text-xs md:text-base lg:text-lg ${getDayColor(
                    date,
                    dashboardData
                  )}`}
                >
                  {date.getUTCDate()}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-5 text-[#2F412F]">
              Insights Into A Long Healthspan
            </h2>
            <ul className="list-disc pl-5 text-left w-full">
              <li className="border-b-2 border-gray-100 text-left mb-0">
                Your energy levels are highest after morning meditation
              </li>
              <li className="border-b-2 border-gray-100 text-left mb-0">
                Sleep quality improves significantly on caffeine-free days
              </li>
              <li className="border-b-2 border-gray-100 text-left mb-0">
                Physical activities show strong correlation with improved mood
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="fixed bottom-4 right-4 z-50">
        <a
          href="https://api.whatsapp.com/send/?phone=918431335947&text=Hi%2C%0AI%27m+interested+in+learning+more+about+your+health+products+and+weight+loss+plan.&type=phone_number&app_absent=0"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20"
        >
          <img
            alt="Chat on WhatsApp"
            title="Chat on WhatsApp"
            loading="eager"
            fetchPriority="high"
            width={65}
            height={65}
            src="/whatsappLogo.png"
            className="w-full h-full object-contain"
          />
        </a>
      </div>
    </div>
  );
}

export default WellnessDashboard;
